import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import { Users, MapPin, FileText, CheckCircle, XCircle, AlertCircle, Loader, BarChart3, RefreshCw } from 'lucide-react';
import { getUserDisplayName } from '../utils/userUtils';

interface PendingUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  role: string;
  createdAt: string;
  validationStatus: string;
}

interface PendingPoi {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
  city: string | null;
  country: string | null;
  category: string | null;
  googlePlaceId: string | null;
  googlePhotoReference: string | null;
  createdAt: string;
  validationStatus: string;
  createdBy: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string;
  } | null;
}

interface PendingCard {
  id: number;
  poiId: number;
  poiName: string | null;
  poiCity: string | null;
  poiCountry: string | null;
  category: string | null;
  text: string | null;
  audioUrl: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  languageId: number | null;
  languageCode: string | null;
  languageName: string | null;
  createdAt: string;
  validationStatus: string;
  createdBy: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string;
  } | null;
}

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'pois' | 'cards' | 'analytics'>('users');
  
  // Data state
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [pendingPois, setPendingPois] = useState<PendingPoi[]>([]);
  const [pendingCards, setPendingCards] = useState<PendingCard[]>([]);
  
  // Analytics state
  const [dauData, setDauData] = useState<{ date: string; dau: number }[]>([]);
  const [dauStartDate, setDauStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [dauEndDate, setDauEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Loading states
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingPois, setIsLoadingPois] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Action loading states
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  
  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Rejection reason state
  const [rejectionReason, setRejectionReason] = useState<{ [key: number]: string }>({});
  const [showRejectForm, setShowRejectForm] = useState<{ [key: number]: boolean }>({});

  // Check if user is admin
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  // Load pending users
  useEffect(() => {
    if (activeTab === 'users') {
      loadPendingUsers();
    }
  }, [activeTab]);

  // Load pending POIs
  useEffect(() => {
    if (activeTab === 'pois') {
      loadPendingPois();
    }
  }, [activeTab]);

  // Load pending cards
  useEffect(() => {
    if (activeTab === 'cards') {
      loadPendingCards();
    }
  }, [activeTab]);

  // Load analytics
  useEffect(() => {
    if (activeTab === 'analytics') {
      loadDAUData();
    }
  }, [activeTab, dauStartDate, dauEndDate]);

  const loadPendingUsers = async () => {
    setIsLoadingUsers(true);
    setError(null);
    try {
      const response = await adminAPI.getPendingUsers();
      setPendingUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pending users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadPendingPois = async () => {
    setIsLoadingPois(true);
    setError(null);
    try {
      const response = await adminAPI.getPendingPois();
      setPendingPois(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pending POIs');
    } finally {
      setIsLoadingPois(false);
    }
  };

  const loadPendingCards = async () => {
    setIsLoadingCards(true);
    setError(null);
    try {
      const response = await adminAPI.getPendingCards();
      setPendingCards(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pending cards');
    } finally {
      setIsLoadingCards(false);
    }
  };

  const loadDAUData = async () => {
    setIsLoadingAnalytics(true);
    setError(null);
    try {
      const response = await adminAPI.getDAURange(dauStartDate, dauEndDate);
      if (response.success) {
        // Convert the results object to an array of {date, dau} objects
        const data = Object.entries(response.data.results).map(([date, dau]) => ({
          date,
          dau: dau as number
        })).sort((a, b) => a.date.localeCompare(b.date));
        setDauData(data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load DAU analytics');
      setDauData([]);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleApproveUser = async (userId: number) => {
    setActionLoading({ ...actionLoading, [`user-${userId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.approveUser(userId);
      setSuccess(response.message);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setActionLoading({ ...actionLoading, [`user-${userId}`]: false });
    }
  };

  const handleRejectUser = async (userId: number) => {
    setActionLoading({ ...actionLoading, [`user-${userId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.rejectUser(userId);
      setSuccess(response.message);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setActionLoading({ ...actionLoading, [`user-${userId}`]: false });
    }
  };

  const handleApprovePoi = async (poiId: number) => {
    setActionLoading({ ...actionLoading, [`poi-${poiId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.approvePoi(poiId);
      setSuccess(response.message);
      setPendingPois(pendingPois.filter(p => p.id !== poiId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve POI');
    } finally {
      setActionLoading({ ...actionLoading, [`poi-${poiId}`]: false });
    }
  };

  const handleRejectPoi = async (poiId: number) => {
    const reason = rejectionReason[poiId];
    if (!reason || reason.trim().length === 0) {
      setError('Please provide a rejection reason');
      return;
    }

    setActionLoading({ ...actionLoading, [`poi-${poiId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.rejectPoi(poiId, reason);
      setSuccess(response.message);
      setPendingPois(pendingPois.filter(p => p.id !== poiId));
      setShowRejectForm({ ...showRejectForm, [poiId]: false });
      setRejectionReason({ ...rejectionReason, [poiId]: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject POI');
    } finally {
      setActionLoading({ ...actionLoading, [`poi-${poiId}`]: false });
    }
  };

  const handleApproveCard = async (cardId: number) => {
    setActionLoading({ ...actionLoading, [`card-${cardId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.approveCard(cardId);
      setSuccess(response.message);
      setPendingCards(pendingCards.filter(c => c.id !== cardId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve card');
    } finally {
      setActionLoading({ ...actionLoading, [`card-${cardId}`]: false });
    }
  };

  const handleRejectCard = async (cardId: number) => {
    setActionLoading({ ...actionLoading, [`card-${cardId}`]: true });
    setError(null);
    setSuccess(null);
    try {
      const response = await adminAPI.rejectCard(cardId);
      setSuccess(response.message);
      setPendingCards(pendingCards.filter(c => c.id !== cardId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject card');
    } finally {
      setActionLoading({ ...actionLoading, [`card-${cardId}`]: false });
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 dark:text-gray-500">Manage pending users, POIs, and cards</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 flex items-start">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-500 hover:text-green-700"
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`${
                activeTab === 'users'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Users className="h-5 w-5 mr-2" />
              Pending Users ({pendingUsers.length})
            </button>
            <button
              onClick={() => setActiveTab('pois')}
              className={`${
                activeTab === 'pois'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <MapPin className="h-5 w-5 mr-2" />
              Pending POIs ({pendingPois.length})
            </button>
            <button
              onClick={() => setActiveTab('cards')}
              className={`${
                activeTab === 'cards'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FileText className="h-5 w-5 mr-2" />
              Pending Cards ({pendingCards.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`${
                activeTab === 'analytics'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              Analytics
            </button>
          </nav>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            {isLoadingUsers ? (
              <div className="flex justify-center items-center py-12">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : pendingUsers.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pending users</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">All user accounts have been reviewed.</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {pendingUsers.map((user) => (
                    <li key={user.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              {getUserDisplayName(user)}
                            </h3>
                            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              {user.role}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">{user.email}</p>
                          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            Registered: {new Date(user.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-4 flex space-x-2">
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            disabled={actionLoading[`user-${user.id}`]}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading[`user-${user.id}`] ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRejectUser(user.id)}
                            disabled={actionLoading[`user-${user.id}`]}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading[`user-${user.id}`] ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* POIs Tab */}
        {activeTab === 'pois' && (
          <div>
            {isLoadingPois ? (
              <div className="flex justify-center items-center py-12">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : pendingPois.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <MapPin className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pending POIs</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">All POIs have been reviewed.</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {pendingPois.map((poi) => (
                    <li key={poi.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{poi.name}</h3>
                            {poi.category && (
                              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                {poi.category}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 space-y-1">
                            <p>
                              📍 Coordinates: {parseFloat(poi.latitude).toFixed(6)}, {parseFloat(poi.longitude).toFixed(6)}
                            </p>
                            {(poi.city || poi.country) && (
                              <p>
                                🌍 Location: {[poi.city, poi.country].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {poi.createdBy && (
                              <p>
                                👤 Created by: {getUserDisplayName(poi.createdBy)} ({poi.createdBy.email})
                              </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Created: {new Date(poi.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="ml-4 flex flex-col space-y-2">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApprovePoi(poi.id)}
                              disabled={actionLoading[`poi-${poi.id}`]}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading[`poi-${poi.id}`] ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => setShowRejectForm({ ...showRejectForm, [poi.id]: !showRejectForm[poi.id] })}
                              disabled={actionLoading[`poi-${poi.id}`]}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </button>
                          </div>
                          {showRejectForm[poi.id] && (
                            <div className="mt-2 w-64">
                              <textarea
                                value={rejectionReason[poi.id] || ''}
                                onChange={(e) => setRejectionReason({ ...rejectionReason, [poi.id]: e.target.value })}
                                placeholder="Reason for rejection..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                rows={3}
                              />
                              <button
                                onClick={() => handleRejectPoi(poi.id)}
                                disabled={actionLoading[`poi-${poi.id}`]}
                                className="mt-2 w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {actionLoading[`poi-${poi.id}`] ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Confirm Rejection'
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Cards Tab */}
        {activeTab === 'cards' && (
          <div>
            {isLoadingCards ? (
              <div className="flex justify-center items-center py-12">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : pendingCards.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pending cards</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">All cards have been reviewed.</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {pendingCards.map((card) => (
                    <li key={card.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              Card for: {card.poiName || `POI #${card.poiId}`}
                            </h3>
                            {card.category && (
                              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {card.category}
                              </span>
                            )}
                            {card.languageCode && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                {card.languageCode}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 space-y-1">
                            {(card.poiCity || card.poiCountry) && (
                              <p>
                                🌍 POI Location: {[card.poiCity, card.poiCountry].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {card.text && (
                              <p className="mt-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">Text:</span>{' '}
                                {card.text.length > 200 ? `${card.text.substring(0, 200)}...` : card.text}
                              </p>
                            )}
                            {card.audioUrl && (
                              <p>
                                🎵 Audio: <a href={card.audioUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">View</a>
                              </p>
                            )}
                            {card.videoUrl && (
                              <p>
                                🎥 Video: <a href={card.videoUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">View</a>
                              </p>
                            )}
                            {card.imageUrl && (
                              <p>
                                🖼️ Image: <a href={card.imageUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">View</a>
                              </p>
                            )}
                            {card.createdBy && (
                              <p>
                                👤 Created by: {getUserDisplayName(card.createdBy)} ({card.createdBy.email})
                              </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Created: {new Date(card.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="ml-4 flex space-x-2">
                          <button
                            onClick={() => handleApproveCard(card.id)}
                            disabled={actionLoading[`card-${card.id}`]}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading[`card-${card.id}`] ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRejectCard(card.id)}
                            disabled={actionLoading[`card-${card.id}`]}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading[`card-${card.id}`] ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {isLoadingAnalytics ? (
              <div className="flex justify-center items-center py-12">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Daily Active Users (DAU)</h2>
                  
                  {/* Date Range Selector */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                      <div className="flex-1 min-w-[200px]">
                        <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          id="start-date"
                          value={dauStartDate}
                          onChange={(e) => setDauStartDate(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                        />
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          id="end-date"
                          value={dauEndDate}
                          onChange={(e) => setDauEndDate(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                        />
                      </div>
                      <button
                        onClick={loadDAUData}
                        disabled={isLoadingAnalytics}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingAnalytics ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        <span className="ml-2">Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* DAU Chart */}
                  {dauData.length === 0 ? (
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-8 text-center">
                      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">No DAU data available for the selected date range</p>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6">
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          DAU Trend ({dauData.length} days)
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Total unique users: {dauData.reduce((sum, d) => sum + d.dau, 0).toLocaleString()}
                        </p>
                      </div>
                      
                      {/* Simple Bar Chart */}
                      <div className="space-y-3">
                        {dauData.map((item) => {
                          const maxDau = Math.max(...dauData.map(d => d.dau));
                          const percentage = maxDau > 0 ? (item.dau / maxDau) * 100 : 0;
                          
                          return (
                            <div key={item.date} className="flex items-center gap-3">
                              <div className="w-24 text-sm text-gray-600 dark:text-gray-400 font-mono">
                                {item.date}
                              </div>
                              <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-8 relative overflow-hidden">
                                <div
                                  className="bg-primary-500 h-full rounded-full transition-all duration-300 flex items-center justify-end pr-3"
                                  style={{ width: `${percentage}%` }}
                                >
                                  {item.dau > 0 && (
                                    <span className="text-white text-sm font-semibold">
                                      {item.dau.toLocaleString()}
                                    </span>
                                  )}
                                </div>
                                {item.dau === 0 && (
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                                    0
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary Stats */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Average DAU</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {Math.round(dauData.reduce((sum, d) => sum + d.dau, 0) / dauData.length).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Peak DAU</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {Math.max(...dauData.map(d => d.dau)).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Lowest DAU</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {Math.min(...dauData.map(d => d.dau)).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
